// Saint Louis University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - SLU has posted no current Common Data Set: the newest on its Office of
//    Institutional Research pages is 2021-2022. The first-year counts come
//    from the U.S. Department of Education's IPEDS Admissions survey for Fall
//    2024, first-time degree-seeking students (College Navigator and the
//    ADM2024 complete data file, unit 179159), so cdsCycle is null and there
//    is no residency table or early-round count.
//  - SLU Admission: Admission Deadlines (Key Dates and Deadlines and the four
//    application paths, under "Apply to Summer or Fall 2027") and Freshman
//    Application Instructions (test-optional policy, program requirements for
//    nursing, occupational therapy, physical therapy, flight science and
//    music, and the separate honors, scholars and special program
//    applications). Quirk: the deadlines page lists the Scholars Programs
//    deadline as December 1 in its key dates, but its Early Decision I
//    section says most honors, scholars and special program applications are
//    due January 5; the page names both and points to the program.
//  - SLU Financial Aid, Cost of Attendance, 2026-27 Undergraduate (full-time,
//    resident on campus). Its items sum to its $83,710 total. The same page
//    gives "Full-Time Tuition Per Term: $28,480", which doubles to the
//    2025-26 tuition ($56,960), not the $58,960 the 2026-27 table lists; the
//    page uses the table. "Housing - Billable" is SLU's own line and the page
//    does not list food separately.
//  - College Scorecard, school id 179159 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=179159#admsns',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'SLU Admission, Admission Deadlines',
        url: 'https://www.slu.edu/admission/freshman/deadlines.php',
        read: '2026-10-01',
    },
    instructions: {
        label: 'SLU Admission, Freshman Application Instructions and Test-Optional Policy',
        url: 'https://www.slu.edu/admission/freshman/instructions.php',
        read: '2026-10-01',
    },
    coa: {
        label: 'SLU Financial Aid, Cost of Attendance',
        url: 'https://www.slu.edu/financial-aid/tuition-and-costs/cost-of-attendance.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?179159-Saint-Louis-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'saint-louis-university',
    kbId: 'saint_louis_university',
    name: 'Saint Louis University',
    shortName: 'SLU',
    aliases: ['SLU'],
    location: 'St. Louis, MO',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (SLU Marketing and Communications,
    // colors and fonts); decoration only.
    branding: {
        primary: '#003DA5',
        secondary: '#C8C9C7',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/saint_louis_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 15533,
        admitted: 11656,
        enrolled: 1615,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'SLU publishes no first-year acceptance rate by college or major, and the newest Common Data Set it has posted is for 2021-2022, so this page uses the federal IPEDS count for Fall 2024.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'SLU has two binding early decision rounds and a non-binding early action round. It has posted no current Common Data Set, and IPEDS does not count early applicants, so there is no early acceptance rate.',
        source: 'deadlines',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Several programs have earlier deadlines or ask for more:',
        rows: [
            { school: 'Nursing', requirement: 'Apply by December 1, with supporting documents by December 11. You need at least a 3.20 high school GPA (4.00 scale), with an emphasis on math and science. Test scores are optional but preferred; if you send one, the minimum is a 24 ACT or 1160 SAT, and it must arrive by January 12, 2027.', source: 'instructions' },
            { school: 'Occupational therapy and physical therapy', requirement: 'Apply by December 1, with supporting documents by December 11. You need at least a 3.20 high school GPA (4.00 scale), with an emphasis on math and science, plus set math and science courses. Test scores are optional; if you send one, it must arrive by January 12, 2027.', source: 'instructions' },
            { school: 'Flight science and aviation management', requirement: 'Apply by December 1 if you want to start flight training in your first semester. After that, admission to these programs is not guaranteed and you may be waitlisted until space opens. Flight science test scores, if you send them, must arrive by January 5, 2027.', source: 'instructions' },
            { school: 'Music', requirement: 'An audition is required, on campus or by video through Get Accept\'d.', source: 'instructions' },
            { school: 'Honors, scholars and special programs', requirement: 'The University Honors Program, the Medical, Law, Physician Assistant and Nutrition and Dietetics Scholars programs and the competitive scholarships each have a separate application, open from September 1 in your applicant portal once you have applied to SLU. Some ask for a resume or letters of recommendation. SLU\'s key dates list December 1 for scholars programs, while its early decision section says most of these applications are due January 5, so check the program\'s own page.', source: 'deadlines' },
        ],
        source: 'instructions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early Action', value: 'December 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 2', decision: 'Begins December 1', binding: true },
            { plan: 'Early Action', deadline: 'December 1', decision: 'By February 1', binding: false },
            { plan: 'Early Decision II', deadline: 'January 19', decision: 'Begins February 1', binding: true },
            { plan: 'Regular Decision', deadline: 'Preferred by April 1', decision: 'By April 15', binding: false },
        ],
        note: 'There is no application fee. Regular Decision answers come by April 15 if your application is complete by March 16, or within 30 days of a complete application; after that, SLU admits and awards merit scholarships while space and funding last. Deferred decisions come in February. Flight science, nursing, occupational therapy and physical therapy close on December 1. If you are denied in an early round, you cannot apply again in Regular Decision.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding: if admitted, you commit to attend and must withdraw all other applications, and all parties must sign an Early Decision Agreement. If your choice depends on getting into the Honors Program, Medical Scholars or another special program, SLU says to apply Early Action or Regular Decision instead.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SLU is test-optional: you choose whether to send ACT or SAT scores, and they are not required for admission or merit scholarships. If you send scores, SLU does not superscore, so send your composite and all subscores from each completed test. Some programs (flight science, nursing, occupational and physical therapy) may prefer a score.',
        source: 'instructions',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'instructions',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 58960 },
            { item: 'Fees', amount: 1000 },
            { item: 'Housing (on campus, billed by SLU)', amount: 16360 },
            { item: 'Books and supplies', amount: 1290 },
            { item: 'Transportation', amount: 3220 },
            { item: 'Miscellaneous', amount: 2880 },
        ],
        total: 83710,
        note: 'For a full-time undergraduate living on campus, fall and spring. SLU notes that additional fees may apply.',
        source: 'coa',
        where: '2026-27 Undergraduate Cost of Attendance',
        netPrice: {
            average: 29674,
            byIncome: [
                { income: '$0 to $30,000', amount: 24520 },
                { income: '$30,001 to $48,000', amount: 26308 },
                { income: '$48,001 to $75,000', amount: 27326 },
                { income: '$75,001 to $110,000', amount: 30576 },
                { income: 'Over $110,000', amount: 33811 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from SLU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
